import './wdyr'
import type { ReactNode } from 'react'

import { GlobalStyle as DSGlobalStyle } from '@monthio/design-system'
import {
	AllLanguagesProvider,
	DesignSystemThemeProvider,
	GlobalStyle,
	LanguageProvider,
	MaterialUiDesignSystemThemeProvider,
	ModalProvider,
	NotificationManager,
	platformService,
	trackingService,
} from '@monthio/react-components'
import {
	captureException,
	init,
	makeFetchTransport,
	makeMultiplexedTransport,
	moduleMetadataIntegration,
	reactRouterV5BrowserTracingIntegration,
} from '@sentry/react'
import config from '#config'
import { createBrowserHistory } from 'history'
import { SnackbarProvider } from 'notistack'
import { StrictMode, Suspense, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { matchPath, MemoryRouter } from 'react-router-dom'
import { createGlobalStyle } from 'styled-components'

import languages from '../translations/locales.json'
import { budgetApiClient } from './api/budgetApiClient'
import { exchangeTokenTransfer } from './api/pensionApi'
import { DevTools } from './components/DevTools'
import { localOverridesService } from './components/DevTools/localOverridesServices'
import { InitialFlowLanguageSetter } from './components/InitialFlowLanguageSetter'
import { LanguageChangeListener } from './components/LanguageChangeListener'
import './windowGlobals'
import { StuckPage } from './components/StuckPage'
import { DevRoutes } from './devRoutes'
import getQueryStringParams from './getQueryStringParameters'
import IntercomConfigurator from './intercomConfigurator'
import { handleRedirect } from './redirectReceivers/redirectHandler'
import Routes from './routes'
import { flowOrchestrationService } from './services/flowOrchestrationService'
import { loadingContainerService } from './services/loadingContainerService'
import { openReplayService } from './services/openReplayService'
import { statisticsReportTelemetryService } from './services/statisticsReportTelemetryService'
import { AccessibleOtherTabOpenedModal } from './services/tabManagerService/otherTabOpenedModalRender'
import configureStore from './store/configureStore'
import { getLocalizedLanguageCode } from './utils/languageCodeMapper'
import './i18n'

const history = createBrowserHistory()
window.History = history

platformService.initialize()
trackingService.initialize()
localOverridesService.initialize()
statisticsReportTelemetryService.initialize()
budgetApiClient.initialize()
openReplayService.initialize()
flowOrchestrationService.initialize()

const routes = Object.values(config.routeCodes).map((route) => ({
	path: route,
}))

const EXTRA_KEY = 'ROUTE_TO'

const transport = makeMultiplexedTransport(makeFetchTransport, (args) => {
	const event = args.getEvent()
	if (
		event?.extra &&
		EXTRA_KEY in event.extra &&
		Array.isArray(event.extra[EXTRA_KEY])
	) {
		return event.extra[EXTRA_KEY]
	}
	return []
})

init({
	dsn: config.sentryDsn,
	debug:
		platformService.getEnvironment() !== 'production' &&
		platformService.getEnvironment() !== 'test',
	environment: config.environment,
	release: process.env.SENTRY_RELEASE_COMMIT,
	attachStacktrace: true,
	integrations: [
		reactRouterV5BrowserTracingIntegration({ history, matchPath, routes }),
		moduleMetadataIntegration(),
	],
	ignoreErrors: [
		// Ignore Meta app webview errors
		'Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone',
		// Ignore extension related errors
		'Invalid call to runtime.sendMessage(). Tab not found.',
		// Ignore iOS webview error
		"undefined is not an object (evaluating 'window.webkit.messageHandlers')",
		// Ignore all `ResizeObserver loop limit exceeded` errors
		'ResizeObserver loop limit exceeded',
		'ResizeObserver loop completed with undelivered notifications.',
		// Ignore peerjs errors
		'This peer cannot reconnect to the server. It has already been destroyed.',
		'ERROR PeerJS:  Error: The current browser does not support WebRTC',
		// Ignore badly shaped api call throws
		'Non-Error promise rejection captured with keys: data, errorCode, message, name, status',
		'Non-Error promise rejection captured with keys: errorCode, errorObject, message, name',
	],
	tracesSampleRate: config.isProduction || config.isTest ? 0.1 : 1.0,
	tracePropagationTargets: config.sentryTracingDomains,
	sendDefaultPii: true,
	transport,
	beforeSend: (event) => {
		if (event?.exception?.values?.[0]?.stacktrace?.frames != null) {
			const { frames } = event.exception.values[0].stacktrace

			// Find the last frame with module metadata containing a DSN
			const routeTo = frames
				.filter((frame) => frame.module_metadata?.dsn != null)
				.map((v) => v.module_metadata)
				.slice(-1) // using top frame only

			if (routeTo.length) {
				event.extra = {
					...event.extra,
					[EXTRA_KEY]: routeTo,
				}
			}
		}

		return event
	},
})

// Global error handler for chunk loading errors
window.addEventListener('error', (event) => {
	const isChunkLoadError =
		event.error?.name === 'ChunkLoadError' ||
		event.error?.message?.includes('Loading chunk') ||
		event.error?.message?.includes(
			'Failed to fetch dynamically imported module',
		) ||
		event.message?.includes("Unexpected token '<'") ||
		(event.filename && event.filename.includes('.bundle.js'))

	if (isChunkLoadError) {
		captureException(event.error || new Error(event.message), {
			tags: {
				errorType: 'GlobalChunkLoadError',
			},

			extra: {
				filename: event.filename,
				lineno: event.lineno,
				colno: event.colno,
				currentUrl: window.location.href,
				errorMessage: event.message,
				eventError: event.error
					? {
							name: event.error.name,
							message: event.error.message,
							stack: event.error.stack,
						}
					: null,
			},
		})

		console.error('Global chunk loading error detected:', {
			filename: event.filename,
			message: event.message,
			url: window.location.href,
			error: event.error,
		})
	}
})

const renderFlow = (children: ReactNode) => {
	const store = configureStore()

	store.subscribe(() => {
		const state = store?.getState()
		const applicantData = {
			applicantId: state?.authentication?.applicantId,
			applicantSessionId:
				state?.authentication?.smartCheckAuthenticationDto?.applicantSessionId,
			applicantConsumerId:
				state?.authentication?.smartCheckAuthenticationDto?.consumerId,
			isFinalFlow:
				state?.authentication?.smartCheckAuthenticationDto?.isFinalFlow,
			partOfMultiFlow:
				state?.authentication?.smartCheckAuthenticationDto?.partOfMultiFlow,
			clientId: state.authentication.smartCheckAuthenticationDto?.client.id,
			regionId: state.authentication.smartCheckAuthenticationDto?.regionId,
		}
		statisticsReportTelemetryService.setApplicantData(applicantData)
		openReplayService.setMetaData(applicantData)
		openReplayService.setUser(applicantData.applicantId)
		flowOrchestrationService.updateStoreState(state)
	})

	ReactDOM.render(
		<StrictMode>
			<Suspense fallback={<ShowLoader loaderLocation="Suspense(main)" />}>
				<MemoryRouter>
					<FlowThemeProviders>
						<AccessibleOtherTabOpenedModal />
						<Provider store={store}>
							<AllLanguagesProvider languages={languages}>
								<LanguageProvider language={getLocalizedLanguageCode()}>
									<NotificationManager />
									<SnackbarProvider maxSnack={5}>
										<InitialFlowLanguageSetter
											initialLanguageDetected={config.initialLanguageDetected}
										/>
										<LanguageChangeListener />
										<ModalProvider>{children}</ModalProvider>
										<DevTools />
									</SnackbarProvider>
								</LanguageProvider>
								{platformService.getEnvironment() === 'production' ? (
									<IntercomConfigurator />
								) : null}
							</AllLanguagesProvider>
						</Provider>
					</FlowThemeProviders>
				</MemoryRouter>
			</Suspense>
		</StrictMode>,
		document.getElementById('root'),
	)
}

const OverrideHtmlFontAndColor = createGlobalStyle`
	:root {
		--flow-font-family: Inter, sans-serif;
		--flow-default-color: hsl(220, 12%, 15%);
	}
`

const FlowThemeProviders = ({ children }: { children: ReactNode }) => {
	return (
		<MaterialUiDesignSystemThemeProvider>
			<DesignSystemThemeProvider>
				<GlobalStyle />
				<DSGlobalStyle $usage="flow" />
				<OverrideHtmlFontAndColor />
				{children}
			</DesignSystemThemeProvider>
		</MaterialUiDesignSystemThemeProvider>
	)
}

interface ShowLoaderProps {
	loaderLocation: string
}

const ShowLoader = ({ loaderLocation }: ShowLoaderProps) => {
	useEffect(() => {
		loadingContainerService.show()
		console.log(
			'loadingContainerService.show(): moduleCompleted',
			'ShowLoader',
			loaderLocation,
		)
		return () => {
			loadingContainerService.hide()
			console.log(
				'loadingContainerService.hide(): moduleCompleted',
				'ShowLoader',
				loaderLocation,
			)
		}
	}, [])

	return null
}

const renderStuckPage = () => {
	ReactDOM.render(
		<StrictMode>
			<Suspense fallback={<ShowLoader loaderLocation="StuckPage" />}>
				<FlowThemeProviders>
					<StuckPage />
				</FlowThemeProviders>
			</Suspense>
		</StrictMode>,
		document.getElementById('root'),
	)
}

const bankAddedSuccessfully = '/bank-added-successfully'
//	Main
const main = async () => {
	console.debug('Redirect url:', history.location.pathname)
	const queryParameters = getQueryStringParams(window.location.search)

	if (queryParameters.devroutes === 'true' && config.isDevelopment) {
		ReactDOM.render(
			<StrictMode>
				<Suspense fallback={<ShowLoader loaderLocation="Suspense(main)" />}>
					<MemoryRouter>
						<FlowThemeProviders>
							<AccessibleOtherTabOpenedModal />
							<LanguageProvider language={getLocalizedLanguageCode()}>
								<NotificationManager />
								<SnackbarProvider maxSnack={5}>
									<ModalProvider>
										<div style={{ width: '1000px', margin: 'auto' }}>
											<DevRoutes></DevRoutes>
										</div>
									</ModalProvider>
								</SnackbarProvider>
							</LanguageProvider>
						</FlowThemeProviders>
					</MemoryRouter>
				</Suspense>
			</StrictMode>,
			document.getElementById('root'),
		)
		return
	}

	if (history.location.pathname.includes('redirect-receiver/mitid')) {
		const url = await exchangeTokenTransfer(
			queryParameters.code,
			queryParameters.state,
		)
		window.location.href = url
		loadingContainerService.hide()
		console.log(
			'loadingContainerService.hide(): moduleCompleted',
			'StuckPage',
			'redirect-receiver/mitid',
		)
		renderStuckPage()
		return
	}

	if (history.location.pathname.includes('redirect-receiver')) {
		await handleRedirect()
		loadingContainerService.hide()
		console.log(
			'loadingContainerService.hide(): moduleCompleted',
			'StuckPage',
			'redirect-receiver',
		)
		renderStuckPage()
		return
	}

	if (history.location.pathname === bankAddedSuccessfully) {
		loadingContainerService.hide()
		console.log(
			'loadingContainerService.hide(): moduleCompleted',
			'StuckPage',
			'bank-added-successfully',
		)

		renderStuckPage()
		return
	}

	renderFlow(<Routes history={history} />)
}

main()
